import { getAdminToken, getToken, updateBudyStatus } from "auth/helpers/authApi";
import { UserBuddyStatus } from "pages/employee/types";
import { apiClient } from "utils/apiClient";

/**
 * Accepts a buddy duty request for a specific employee.
 * @param id - The ID of the employee.
 * @returns The response data from the API.
 */
export const acceptBuddyDuty = async (id: string) => {
    const token = getToken();
    const response = await apiClient(`employees/${id}/buddy/accept`, {
        method: "PUT",
        token,
    });
    updateBudyStatus(UserBuddyStatus.INCOMPLETE_QUESTIONAIRE);

    return response.data;
}

/**
 * Rejects a buddy duty request for a specific employee.
 * @param id - The ID of the employee.
 * @returns The response data from the API.
 */
export const rejectBuddyDuty = async (id: string) => {
    const token = getToken();
    const response = await apiClient(`employees/${id}/buddy/reject`, {
        method: "PUT",
        token,
    });
    updateBudyStatus(UserBuddyStatus.REJECTED);

    return response.data;
}

/**
 * Pauses a buddy duty for a specific employee.
 * @param id - The ID of the employee.
 * @param data - The data object containing the date.
 * @returns The response data from the API.
 */
export const pauseBuddyDuty = async (id: string, data: {
    buddyDutyPausedUntil: Date
}) => {
    const token = getAdminToken();
    const response = await apiClient(`employees/${id}/buddy/pause`, {
        method: "PUT",
        token,
        data
    });

    return response.data;
}

/**
 * Resumes a paused buddy duty for a specific employee.
 * @param id - The ID of the employee.
 * @returns The response data from the API.
 */
export const resumeBuddyDuty = async (id: string) => {
    const token = getAdminToken();
    const response = await apiClient(`employees/${id}/buddy/resume`, {
        method: "PUT",
        token,
    });

    return response.data;
}

/**
 * Activates a buddy duty for a specific employee.
 * @param id - The ID of the employee.
 * @returns The response data from the API.
 */
export const activateBuddyDuty = async (id: string) => {
    const token = getAdminToken();
    const response = await apiClient(`employees/${id}/buddy/activate`, {
        method: "PUT",
        token,
    });

    return response.data;
}

/**
 * Deactivates a buddy duty for a specific employee.
 * @param id - The ID of the employee.
 * @returns The response data from the API.
 */
export const deactivateBuddyDuty = async (id: string) => {
    const token = getAdminToken();
    const response = await apiClient(`employees/${id}/buddy/deactivate`, {
        method: "PUT",
        token,
    });

    return response.data;
}

/**
 * Invites an employee to become a buddy.
 * @param id - The ID of the employee.
 * @returns The response data from the API.
 */
export const inviteBuddyDuty = async (id: string) => {
    const token = getAdminToken();
    const response = await apiClient(`employees/${id}/buddy/invite`, {
        method: "POST",
        token,
    });

    return response.data;
}


/**
 * Gets the activities for a buddy duty for a specific employee.
 * @returns The response data from the API.
 */
export const getBuddyActivities = async (id: string) => {
    const token = getToken();
    const response = await apiClient(`employees/${id}/buddy/activities`, {
        method: "GET",
        token,
    });

    return response.data;
}
